import { Button, Dialog, DropdownMenu, IconButton } from '@radix-ui/themes';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { apiFetch } from '../../utils/api';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import ReadInterview from './ReadInterview';

const DeleteCandidateModal = ({
  show,
  onClose,
  deleteApplication,
}: {
  show: boolean;
  onClose: (close: boolean) => void;
  deleteApplication: { deleteApplicationCallback: () => Promise<void>; loading: boolean };
}) => {
  const { t } = useTranslation();
  const { deleteApplicationCallback, loading } = deleteApplication;
  const deleteConfirmed = async () => {
    await deleteApplicationCallback();
    onClose(false);
  };

  return (
    <Dialog.Root open={show} onOpenChange={() => onClose(false)}>
      <Dialog.Content>
        <Dialog.Title>{t('candidateDropdown.deleteDialog.title')}</Dialog.Title>
        <Dialog.Description>{t('candidateDropdown.deleteDialog.description')}</Dialog.Description>

        <div className="flex w-full">
          <div className="w-1/2 px-4">
            <Button
              size="3"
              color="gray"
              variant="soft"
              className="w-full"
              disabled={loading}
              onClick={() => onClose(false)}
            >
              {t('candidateDropdown.deleteDialog.cancelButton')}
            </Button>
          </div>
          <div className="w-1/2 px-4">
            <Button size="3" color="red" className="w-full" loading={loading} onClick={() => deleteConfirmed()}>
              {t('candidateDropdown.deleteDialog.deleteButton')}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const CandidateDropdown = ({
  application,
  deleteApplication,
}: {
  application: {
    id: string;
    candidate: { id: string };
    job: { id: string; integration: { teamtailor: { companyId: string } } };
    integration: { teamtailor: { jobId: string; candidateId: string } };
    interviews: { nodes: { id: string }[] };
  };
  deleteApplication?: { deleteApplicationCallback: () => Promise<void>; loading: boolean };
}) => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInterview, setShowInterview] = useState(false);

  const navigate = useNavigate();

  const viewJob = () => {
    navigate('/job/' + application.job.id);
  };

  const viewTeamtailor = () => {
    const integration = application.integration.teamtailor;
    const companyId = application.job.integration.teamtailor.companyId;
    window.open(
      `https://app.teamtailor.com/companies/${companyId}/jobs/${integration.jobId}/stages/candidate/${integration.candidateId}`,
    );
  };

  const viewCandidateProfile = () => {
    const candidateId = application.candidate.id;
    navigate(`/candidates/${candidateId}`);
  };

  const viewOpenApplication = () => {
    track(AnalyticsEvents.SHAREABLE_SUMMARY_OPENED);
    const applicationId = application.id;
    window.open(`/application/share/${applicationId}`);
  };

  const downloadApplicationSummary = async () => {
    track(AnalyticsEvents.SUMMARY_PDF_DOWNLOADED);
    try {
      const res = await apiFetch(`/applications/${application.id}/summary/pdf`);

      if (!res.ok) {
        toast.error(t('candidateDropdown.pdfDownloadErrorToast', { status: res.status, statusText: res.statusText }));
        return;
      }

      const filename = res.headers.get('Content-Disposition')?.split('filename=')?.[1] ?? 'application-summary.pdf';
      const blob = await res.blob();

      const fileURL = URL.createObjectURL(blob);
      const fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.download = decodeURIComponent(filename);
      fileLink.click();
      fileLink.remove();
      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
    }
  };

  return (
    <>
      <ReadInterview
        show={showInterview}
        handleClose={() => setShowInterview(false)}
        id={application.id}
        application={application}
      />

      {deleteApplication && (
        <DeleteCandidateModal
          deleteApplication={deleteApplication}
          show={showDeleteModal}
          onClose={setShowDeleteModal}
        />
      )}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton color="gray" variant="soft" size="3" data-ph-capture-attribute-region="candidate_menu">
            <Ellipsis size={24} />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content color="gray" variant="soft" align="end" data-ph-capture-attribute-region="candidate_menu">
          <DropdownMenu.Item
            onClick={() => {
              track(AnalyticsEvents.INTERVIEW_READ_OPENED);
              setShowInterview(true);
            }}
          >
            {t('candidateDropdown.menu.readInterview')}
          </DropdownMenu.Item>
          {application.integration && application.integration.teamtailor && (
            <DropdownMenu.Item onClick={viewTeamtailor}>
              {t('candidateDropdown.menu.applicationInTeamtailor')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item onClick={viewJob}>{t('candidateDropdown.menu.viewJob')}</DropdownMenu.Item>
          <DropdownMenu.Item onClick={viewCandidateProfile}>
            {t('candidateDropdown.menu.candidateProfile')}
          </DropdownMenu.Item>
          {deleteApplication && (
            <DropdownMenu.Item
              color="red"
              onClick={() => {
                setShowDeleteModal(true);
              }}
            >
              {t('candidateDropdown.menu.deleteApplication')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item onClick={viewOpenApplication}>
            {t('candidateDropdown.menu.shareableSummary')}
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={downloadApplicationSummary}>
            {t('candidateDropdown.menu.summaryAsPdf')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </>
  );
};

export default CandidateDropdown;
